import { baseApi } from '@klp/core/api/services/rtkApi';

import { byggApiRoutes } from './api';
import { Bygg } from './classes';
import type { IBygg, ByggMoterom } from './classes';
import type { ByggMoteromCalendarEntry } from './classes/ByggMoteromCalendarEntry';
import type { ByggMoteromCalendarRequest } from './classes/ByggMoteromCalendarRequest';

interface ByggQueryParams {
    byggNummer: string;
}

interface ByggListQueryParams {
    kundeNummer?: string;
}

interface ByggMoteromQueryParams {
    byggNummer: string;
    kundenummer?: string;
}

export const byggRtkApi = baseApi.injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
        getBygg: builder.query<Bygg, ByggQueryParams>({
            query: ({ byggNummer }) => ({
                url: byggApiRoutes.single(byggNummer),
                method: 'GET',
            }),
            transformResponse: (response: IBygg) => {
                return Bygg.parseObject(response);
            },
            providesTags: (result, error, { byggNummer }) => [
                { type: 'Bygg', id: byggNummer },
            ],
        }),
        getByggList: builder.query<Array<Bygg>, ByggListQueryParams>({
            query: ({ kundeNummer }) => ({
                url: kundeNummer
                    ? byggApiRoutes.listForKunde(kundeNummer)
                    : byggApiRoutes.list(),
                method: 'GET',
            }),
            transformResponse: (response: Array<IBygg>) => {
                return response.map((bygg: IBygg) => Bygg.parseObject(bygg));
            },
            providesTags: (result) => [
                { type: 'Bygg', id: 'LIST' },
                ...(result || []).map(({ byggNummer }) => ({
                    type: 'Bygg' as const,
                    id: byggNummer,
                })),
            ],
        }),
        getByggMoterom: builder.query<
            Array<ByggMoterom>,
            ByggMoteromQueryParams
        >({
            query: ({ byggNummer, kundenummer = '' }) => ({
                url: byggApiRoutes.moteromList(byggNummer, kundenummer),
                method: 'GET',
            }),
            transformResponse: (response: Array<ByggMoterom>) => response,
            providesTags: (result, error, { byggNummer }) => [
                { type: 'ByggMoterom', id: byggNummer },
            ],
        }),
        getByggMoteromCalendar: builder.query<
            Array<ByggMoteromCalendarEntry>,
            ByggMoteromCalendarRequest
        >({
            query: ({ byggNummer, kundenummer, from, to }) => {
                const urlParams = new URLSearchParams({
                    kundenummer,
                    from,
                    to,
                });
                return {
                    url:
                        byggApiRoutes.moteromKalendar(byggNummer) +
                        '?' +
                        urlParams.toString(),
                    method: 'GET',
                };
            },
            transformResponse: (response: Array<ByggMoteromCalendarEntry>) =>
                response,
            providesTags: (result, error, { byggNummer }) => [
                { type: 'ByggMoterom', id: `${byggNummer}-calendar` },
            ],
        }),
    }),
});

export const {
    useGetByggQuery,
    useGetByggListQuery,
    useGetByggMoteromQuery,
    useGetByggMoteromCalendarQuery,
    useLazyGetByggMoteromCalendarQuery,
} = byggRtkApi;
